import React, { useMemo } from 'react'
import {
  Box,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'

import { Card } from '@repo/ui'
import type { TourAnalytics, TourAnalyticsStepRow, TourAnalyticsSummaryRow } from 'services/tourAnalyticsApi'
// Direct subpath rather than the `components/onboarding` barrel — that barrel
// re-exports ToursDialog, which reaches back into `components`, and Header documents
// the same cycle. The registry itself is pure data.
import { TOURS } from 'components/onboarding/tours'
import type { TourDef } from 'components/onboarding/tours'

/**
 * The per-tour adoption table and funnels, shared by every surface that shows tour
 * uptake — `/internal/analytics` (all companies, optionally one person) and the
 * internal company detail page's Tours tab (one company).
 *
 * ## Why this is shared rather than rebuilt per surface
 *
 * working-conventions §71: a detail-page tab renders the SAME thing as its standalone
 * page, parameterised by scope, so a change to one lands on both by construction. The
 * §71 audit found four company-detail panels that had each re-declared their own
 * columns and drifted from the standalone page — one rendered a navigable link where
 * the other rendered dead text, another fell back to plain text where the page drew a
 * status chip. This exists so a fifth does not join them.
 *
 * The genuinely shared unit here is NOT the page. `ToursPanel` also carries a scope
 * input, the tour Catalog and the Anchor contract, and none of those are company-
 * scoped — the catalog and anchor list are the global registry, identical on every
 * company, so putting them in a company tab would be noise wearing the costume of
 * data. What is shared is exactly the part that answers "who took these tours":
 * the summary table and the funnels.
 *
 * ## The one semantic that must never drift, and is why `rate` lives here
 *
 * `started === 0` renders an em dash, NEVER "0%". They are different facts — 0% means
 * people started and none finished, a dash means nobody started — and they look
 * identical on a chart, which is why the distinction has to be made in words. If each
 * surface owned its own copy of this, one of them would eventually print the silent
 * zero that reads as "nobody completed it".
 */

/** A rate, or an honest dash. See the note above; the `started === 0` branch is the point.
 *
 *  Clamped at 100 because a tour started 31 days ago and completed yesterday counts in
 *  `completed` but not in `started` at the window boundary. Clamping the DISPLAY is
 *  better than dropping a real completion from the data. */
export const tourRate = (numerator: number, denominator: number): string =>
  denominator === 0
    ? '—'
    : `${Math.min(100, Math.round((numerator / denominator) * 100))}%`

export const TourSectionNote = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
    {children}
  </Typography>
)

export const TourHeadCell = ({
  children,
  align,
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
}) => (
  <TableCell
    align={align ?? 'left'}
    sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}
  >
    {children}
  </TableCell>
)

interface FunnelProps {
  tour: TourDef
  started: number
  /** reached, by step index, for THIS tour. */
  reachedByIndex: Map<number, number>
}

/**
 * One tour's funnel: a rung per CONTENT step, each as a share of that tour's starters.
 *
 * Only `tour.steps` gets a rung. The engine appends a shared outro at index
 * `steps.length` and emits events for it (§48), so the raw data carries one more index
 * than the tour has content — drawing it would tell a reader a five-step tour has six,
 * contradicting the "N of 5" counter they saw while taking it.
 */
const TourFunnel = ({ tour, started, reachedByIndex }: FunnelProps) => {
  const theme = useTheme()

  if (started === 0) {
    return (
      <Stack spacing={2}>
        <Typography variant="body2" fontWeight={600}>
          {tour.label}
        </Typography>
        <TourSectionNote>
          Nobody started this tour in the window, so there is no funnel to draw. That is
          an absence of data, not a drop-off.
        </TourSectionNote>
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="baseline" spacing={3}>
        <Typography variant="body2" fontWeight={600}>
          {tour.label}
        </Typography>
        <Typography variant="body3" color="text.secondary">
          {started} {started === 1 ? 'starter' : 'starters'}
        </Typography>
      </Stack>

      <Stack spacing={2}>
        {tour.steps.map((step, index) => {
          const reached = reachedByIndex.get(index) ?? 0
          const pct = Math.min(100, (reached / started) * 100)
          return (
            <Stack key={step.id} spacing={1}>
              <Stack direction="row" justifyContent="space-between" spacing={3}>
                <Typography variant="body3" sx={{ minWidth: 0 }}>
                  {index + 1}. {step.title}
                </Typography>
                <Typography variant="body3" color="text.secondary" whiteSpace="nowrap">
                  {reached} · {tourRate(reached, started)}
                </Typography>
              </Stack>
              {/* The bar is the whole chart: a track at 100% (the starters) with a fill
                  at this rung's share. A rung that is genuinely 0 still shows its empty
                  track, so a cliff reads as a cliff rather than as a missing row. */}
              <Box
                sx={{
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[100],
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${pct}%`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.brand[500],
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            </Stack>
          )
        })}
      </Stack>
    </Stack>
  )
}

interface TourAdoptionProps {
  summary: TourAnalyticsSummaryRow[]
  steps: TourAnalyticsStepRow[]
  /** Prefixed to the Adoption card's note, so each surface can state its own scope
   *  ("Counts are for this company." / staff-run caveats) without forking the shared
   *  wording that follows it. */
  scopeNote?: React.ReactNode
}

export const TourAdoption = ({ summary, steps, scopeNote }: TourAdoptionProps) => {
  const theme = useTheme()

  const reachedByTour = useMemo(() => {
    const map = new Map<string, Map<number, number>>()
    for (const row of steps) {
      const inner = map.get(row.tourKey) ?? new Map<number, number>()
      inner.set(row.stepIndex, row.reached)
      map.set(row.tourKey, inner)
    }
    return map
  }, [steps])

  const summaryByTour = useMemo(() => {
    const map = new Map<string, TourAnalytics['summary'][number]>()
    for (const row of summary) map.set(row.tourKey, row)
    return map
  }, [summary])

  /** Keys with events that no longer have a catalog entry. Named, never hidden — a
   *  figure whose subject no longer exists should say so. */
  const retiredKeys = useMemo(
    () => summary.filter(row => !TOURS.some(t => t.id === row.tourKey)),
    [summary],
  )

  return (
    <>
      <Card title="Adoption">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <TourSectionNote>
            {scopeNote}
            Counts are distinct PEOPLE, not events. Someone who replays a tour or steps
            backwards counts once. Completion and skip are each a share of that
            tour&apos;s starters, so they can add up to more than 100%: one person who
            left a first run early and finished a second is counted in both.
          </TourSectionNote>
          <MuiTable size="small">
            <TableHead>
              <TableRow>
                <TourHeadCell>Tour</TourHeadCell>
                <TourHeadCell align="right">Started</TourHeadCell>
                <TourHeadCell align="right">Completion</TourHeadCell>
                <TourHeadCell align="right">Skip</TourHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TOURS.map(tour => {
                const row = summaryByTour.get(tour.id)
                const started = row?.started ?? 0
                return (
                  <TableRow key={tour.id}>
                    <TableCell sx={{ fontSize: 13 }}>{tour.label}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>
                      {started}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>
                      {tourRate(row?.completed ?? 0, started)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>
                      {tourRate(row?.skipped ?? 0, started)}
                    </TableCell>
                  </TableRow>
                )
              })}
              {retiredKeys.map(row => (
                <TableRow key={row.tourKey}>
                  <TableCell sx={{ fontSize: 13 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <span>{row.tourKey}</span>
                      <Box
                        sx={{
                          px: 2,
                          py: '2px',
                          borderRadius: 1,
                          fontSize: 11,
                          color: theme.palette.text.secondary,
                          backgroundColor: alpha(theme.palette.text.secondary, 0.08),
                        }}
                      >
                        Retired
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {row.started}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {tourRate(row.completed, row.started)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {tourRate(row.skipped, row.started)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
          {retiredKeys.length > 0 && (
            <TourSectionNote>
              A retired row is a tour that has events but no longer exists in the
              registry. Its history is kept and shown separately rather than deleted or
              merged into a total, because a figure whose subject no longer exists
              should say so.
            </TourSectionNote>
          )}
        </Stack>
      </Card>

      <Card title="Funnels">
        <Stack spacing={6} sx={{ mt: 4 }}>
          <TourSectionNote>
            Each rung is the share of that tour&apos;s starters who were seen at that
            step. The closing &quot;Replay this any time&quot; card is not shown: the
            engine appends it to every tour and it is not counted anywhere else either.
          </TourSectionNote>
          {TOURS.map(tour => (
            <TourFunnel
              key={tour.id}
              tour={tour}
              started={summaryByTour.get(tour.id)?.started ?? 0}
              reachedByIndex={reachedByTour.get(tour.id) ?? new Map()}
            />
          ))}
        </Stack>
      </Card>
    </>
  )
}
